import { observable, action, computed, observe, IObjectDidChange, Lambda } from "mobx";
import Debounce from "debounce-decorator";

import { MasterEvent, MultiViewBranchInfo, MultiViewLeagueInfo, Market, Selection, MultiViewBranchType } from "./models";
import * as Parsers from "./parsers";
import {
    MasterEventDynamicObject, MasterEventsContentLegacy, CustomEventData,
    OddsHelper, ParseDate, SelectionType, Position, MasterEventPrefix
} from "./helpers";
import { constants } from "./constants";
import { liveEventsTime, globalTimeTicker, GlobalTimeTicker, DataHelper } from "sb-resp-lib";
import { createTransformer } from "mobx-utils";

import { eBranches, LiveMasterEventLegacy, EventLiveLegacy } from "sb-resp-lib";
import {
    SportContentProviderLegacy, BranchConfigData,
    StatisticsContentProvider, PreLiveMasterEventLegacy,
    EventPreliveLegacy, LeagueInfoLegacy, BranchInfoLegacy
} from "../../../interfaces";

/*
    Tests describing the structure of the ouput data should be written first.
    It would be better to split the tests into few suits:
    - base tests related to class and instance structure
    - business logic
    - edge cases
*/
export type UtilsComposition = { betSlipUtil: BetSlipUtil, oddsUtil: Odds }
export type DataComposition = {
    sportsProvider: SportContentProviderLegacy,
    statisticsProvider: StatisticsContentProvider,
    branchesConfigData: BranchConfigData[],
    utils: UtilsComposition,
    gameStatus: GameStatus,
    timeControl: TimeControl,
    startLiveBettingInMinutes: number
};

export class SportsMultiViewBlockData {
    @observable branchesList: MultiViewBranchInfo[];
    @observable leaguesList: MultiViewLeagueInfo[];
    @observable masterEvents: MasterEventDynamicObject;
    @observable currentSelectedBranch?: MultiViewBranchInfo = undefined;
    @observable currentBranchId: string = "0";

    public currentTimeZone: number;
    public provider: SportContentProviderLegacy;
    public statisticsProvider: StatisticsContentProvider;
    public betSlipUtil: BetSlipUtil;
    public oddsUtil: Odds;
    private gameStatus: GameStatus;
    public navHandler: Application;
    public marketsConfig: any;
    private newMasterEventsIds: MasterEventsContentLegacy;
    private branchesConfigData: BranchConfigData[];
    private eventsManager: EventsManager;
    private timeControl: TimeControl;
    private eventTimeTickingDisposer: Lambda;
    private startLiveBettingInMinutes: number;

    constructor(provider: DataComposition, navHandler: Application) {
        this.provider = provider.sportsProvider;
        this.statisticsProvider = provider.statisticsProvider;
        this.branchesConfigData = Parsers.branchesConfigParser.parseData(provider.branchesConfigData);
        this.gameStatus = provider.gameStatus;
        this.branchesList = [];
        this.leaguesList = [];
        this.masterEvents = {};
        this.newMasterEventsIds = {};
        this.betSlipUtil = provider.utils.betSlipUtil;
        this.oddsUtil = provider.utils.oddsUtil;
        this.navHandler = navHandler;
        this.openEventView = this.openEventView.bind(this);
        this.currentTimeZone = provider.timeControl.getCurrentTimeZone();
        this.eventsManager = new EventsManager();
        this.timeControl = provider.timeControl;
        this.eventTimeTickingDisposer = () => { };
        this.startLiveBettingInMinutes = provider.startLiveBettingInMinutes;
    }

    /* istanbul ignore next */
    public activate() {

        // add event listeners
        this.provider.TodaysEventsCache.addListener(this, this.refreshData);
        this.eventsManager.addEventListener("timezoneChanged", this, this.updateTimeDateOfEvents, true);

        liveEventsTime.setTimeControlProvider(this.timeControl);
        this.updateData();

        this.eventTimeTickingDisposer = observe(globalTimeTicker, (change: IObjectDidChange) => {
            let changedObject = change.object as GlobalTimeTicker;
            this.updateTimeElapsed(changedObject.interval);
        });
    }

    /* istanbul ignore next */
    public deactivate() {

        // remove event listeners
        this.provider.TodaysEventsCache.removeListener(this);
        this.eventsManager.removeEventListener("timezoneChanged", this);
        this.eventTimeTickingDisposer();
    }

    updateTimeDateOfEvents(tzone?: number) {
        this.currentTimeZone = typeof tzone === "number" ? tzone : this.timeControl.getCurrentTimeZone();

        Object.keys(this.masterEvents).forEach(key => {
            if (this.masterEvents[key].isLive === false) {
                let event = this.masterEvents[key];
                let time = ParseDate(event.masterEventDate, this.currentTimeZone).formatDate("hh:mm");
                let date = ParseDate(event.masterEventDate, this.currentTimeZone).formatDate("dd/MM/yy");
                event.updateTimeDate(time, date);
            }
        });
    }

    private processMasterEvents() {

        for (const id in this.masterEvents) {
            if (this.masterEvents.hasOwnProperty(id)) {
                let oldMasterEvent = this.masterEvents[id];
                //prevent duplication of events when in kick off
                if ((!oldMasterEvent.isLive && this.isEventInKickOff(oldMasterEvent.masterEventDate)) || !this.newMasterEventsIds[id]) {
                    this.performDeleteOperation(oldMasterEvent, id);
                }
                else {
                    this.performUpdateOperation(oldMasterEvent, id);
                }
            }
        }

        this.performInsertOperation();
    }

    private getPrefix(me: MasterEvent) {
        return me.isLive ? MasterEventPrefix.Live : MasterEventPrefix.PreLive;
    }

    @action.bound updateMarkets(newMarkets: Market[], masterEventIDWIthPrefix: string, branchID: string): void {
        for (let id = 0; id < Object.keys(newMarkets).length; id += 1) {
            let newMarket = newMarkets[Object.keys(newMarkets)[id]];

            if (this.masterEvents[masterEventIDWIthPrefix]) {

                let currentMarket = this.masterEvents[masterEventIDWIthPrefix].markets.find((market) => market.id === newMarket.id);

                if (!currentMarket) { continue; }

                // remove middle selection for Tennis and Backetball
                this.removeMarketsMiddleSelections(newMarket, branchID);

                this.updateMarket(newMarket, currentMarket)
            }
        }
    }

    /* istanbul ignore next */
    private removeMarketsMiddleSelections(newMarket: Market, branchID: string) {
        let marketKey = constants.typeIDMap[newMarket.typeID];
        marketKey === "FT_ML" &&
            (branchID === eBranches.Basketball.toString() || branchID === eBranches.Tennis.toString()) &&
            newMarket.selections.length === 3 &&
            newMarket.selections.splice(1, 1);
    }

    /* istanbul ignore next */
    private updateMarket(newMarket: Market, currentMarket: Market) {

        currentMarket.updateSelections(newMarket.selections);
    }

    /* istanbul ignore next */
    /* tslint:disable:cyclomatic-complexity */
    private updateMasterEventData(currentMasterEvent: MasterEvent,
        newMasterEvent: LiveMasterEventLegacy | PreLiveMasterEventLegacy) {

        let score1 = newMasterEvent["Score1"];
        let score2 = newMasterEvent["Score2"];
        // update master event data
        currentMasterEvent.danger = newMasterEvent.Danger;
        currentMasterEvent.allMarketsCount = newMasterEvent["Live"] ?
            newMasterEvent["QACount"] * 1 : newMasterEvent["Specials"] * 1;
        currentMasterEvent.gameData = newMasterEvent["GameData"] ? newMasterEvent["GameData"] : "";

        this.updateMasterEventMarkets(currentMasterEvent, newMasterEvent.MultiViewRegularEvents);
        this.updateMasterEventMarkets(currentMasterEvent, newMasterEvent.MultiViewQAEvents);
        newMasterEvent["Live"] && score1 && score2 && currentMasterEvent.updateScore(score1, score2);

        this.updateLiveTimeElapsedOnHalfTime(currentMasterEvent);

        (currentMasterEvent.liveTimeElapsed === 0 && newMasterEvent["Live"]) &&
            (currentMasterEvent.updateLiveTimeElapsed(liveEventsTime.getTime(currentMasterEvent.gameData, parseInt(currentMasterEvent.branchID))));
    }

    /* istanbul ignore next */
    private updateMasterEventMarkets(currentMasterEvent: MasterEvent,
        multiviewEvents: EventPreliveLegacy[] | EventLiveLegacy[]) {

        for (const eventID in multiviewEvents) {
            if (multiviewEvents.hasOwnProperty(eventID)) {
                let newMarket = multiviewEvents[eventID];
                let parsedNewMarkets = Parsers.MarketParser.ParseEvent(newMarket);
                OddsHelper.convertMarketOdds(parsedNewMarkets, this.oddsUtil, currentMasterEvent.branchID);

                for (let id = 0; id < Object.keys(parsedNewMarkets).length; id += 1) {
                    let parsedNewMarket = parsedNewMarkets[id];

                    let currentMarket = currentMasterEvent.markets.find(function (market: Market) {
                        return market.id === parsedNewMarket.id;
                    });

                    if (!currentMarket) {
                        currentMasterEvent.markets.push(parsedNewMarket);
                        continue;
                    }

                    this.updateMarket(parsedNewMarket, currentMarket);
                }
            }
        }
    }

    @action.bound updateData() {

        // create list of masterEventIds from cache
        this.getMasterEventsIdsFromCache(this.provider.TodaysEventsCache.AllLiveMasterEvents);
        this.getMasterEventsIdsFromCache(this.provider.TodaysEventsCache.PreLiveMasterEvents);

        // Parse all new data into our models collections
        this.processMasterEvents();

        // clear temporary data objects
        this.newMasterEventsIds = {};
    }

    @action.bound performUpdateOperation(masterEvent: MasterEvent, id: string) {
        let currentMasterEvent = masterEvent;
        let newMasterEvent = masterEvent.isLive ?
            this.provider.TodaysEventsCache.AllLiveMasterEvents[this.newMasterEventsIds[id].index] :
            this.provider.TodaysEventsCache.PreLiveMasterEvents[this.newMasterEventsIds[id].index]

        this.updateMasterEventData(currentMasterEvent, newMasterEvent);

        delete this.newMasterEventsIds[id];
    }

    @action.bound performDeleteOperation(masterEvent: MasterEvent, masterEventID: string) {

        let league = this.leaguesList && this.leaguesList.find(function (league: MultiViewLeagueInfo) {
            var result = league.id === masterEvent.leagueID ? true : false;
            return result
        });

        if (!league) { return }

        let eventIndex = league.eventIds.findIndex(function (inputID: string) {
            return inputID === masterEventID
        });

        league.eventIds.splice(eventIndex, 1);
        delete this.masterEvents[masterEventID];
    }

    @action.bound public performInsertOperation() {

        let isEventLive: Boolean,
            newEventModel: MasterEvent,
            prefix: String,
            { AllLiveMasterEvents, PreLiveMasterEvents } = this.provider.TodaysEventsCache;

        for (let id in this.newMasterEventsIds) {
            if (this.newMasterEventsIds.hasOwnProperty(id)) {
                isEventLive = this.newMasterEventsIds[id].isLive ? true : false;

                newEventModel = this.extractMasterEvent(this.newMasterEventsIds[id].index, isEventLive, AllLiveMasterEvents, PreLiveMasterEvents);
                if (!newEventModel.isLive && this.isEventInKickOff(newEventModel.masterEventDate)) { continue; }
                prefix = this.getPrefix(newEventModel);

                // Add master new master event
                this.masterEvents[prefix + newEventModel.id] = newEventModel;

                this.processLeagues(newEventModel);
                this.processBranches(newEventModel);
            }
        }
    }
    /* istanbul ignore next */
    @action.bound updateFromPush(_updatedEvents: any) {
        let updatedEvents = _updatedEvents.UpdatedEvents;

        for (let index = 0; index < updatedEvents.length; index++) {

            const event = updatedEvents[index];

            let masterEventID = event.MasterEventID ?
                event.MasterEventID : undefined;

            let prefix = event.Live ? MasterEventPrefix.Live : MasterEventPrefix.PreLive;
            let masterEventIDWIthPrefix = prefix + event.MasterEventID

            if (!masterEventID) { continue; }

            let newMarkets = Parsers.MarketParser.ParseEvent(event);

            if (this.masterEvents[masterEventIDWIthPrefix]) {
                OddsHelper.convertMarketOdds(newMarkets, this.oddsUtil, this.masterEvents[masterEventIDWIthPrefix].branchID);
                this.updateMarkets(newMarkets, masterEventIDWIthPrefix, this.masterEvents[masterEventIDWIthPrefix].branchID);
                this.updateScore(event, this.masterEvents[masterEventIDWIthPrefix]);
            }
        }
    }

    /* istanbul ignore next */
    @action.bound updateScore(event: any, masterEvent: MasterEvent): void {

        event.Score1 && event.Score2 && masterEvent.updateScore(event.Score1, event.Score2)
    }

    /* istanbul ignore next */
    @action.bound updateTimeElapsed(interval: number) {
        let masterEventsKeys = Object.keys(this.masterEvents);

        for (let key = 0; key < masterEventsKeys.length; key++) {
            let masterEvent = this.masterEvents[masterEventsKeys[key]];
            if (masterEvent.liveTimeElapsed !== 0) {
                masterEvent.updateTime(interval);
            }
        }
    }

    @action.bound public processLeagues(newEventModel: MasterEvent) {

        let { LeaguesOrdered } = this.provider.LeaguesCache;

        let league = this.leaguesList.find((league) => {
            return league.id === newEventModel.leagueID;
        });

        if (league) {
            league.addEventId(this.getPrefix(newEventModel) + newEventModel.id);
        } else {
            let leagueModel = this.extractLeague(newEventModel.leagueID, LeaguesOrdered);
            if (!leagueModel) { return; }
            leagueModel.addEventId(this.getPrefix(newEventModel) + newEventModel.id);
            this.leaguesList.push(leagueModel);
        }
    }

    @action.bound public processBranches(newEventModel: MasterEvent) {

        let { BranchesOrdered } = this.provider.BranchesCache;

        let branch = this.branchesList.find((branch) => {
            return branch.id === newEventModel.branchID;
        });

        if (!branch) {
            let branchModel = this.extractBranch(newEventModel.branchID, BranchesOrdered);
            if (!branchModel) { return; }
            this.branchesList.push(branchModel);
            branch = branchModel;
        }

        let isLeagueIdExist = branch.leagueIds.find((leagueId) => {
            return leagueId === newEventModel.leagueID;
        })

        if (!isLeagueIdExist) {
            branch.addLeagueId(newEventModel.leagueID);
        }
    }

    @action.bound public getMasterEventsIdsFromCache(masterEvents: LiveMasterEventLegacy[] | PreLiveMasterEventLegacy[]) {

        let customObj: CustomEventData,
            prefix: String;

        for (var i = 0; i < masterEvents.length; i += 1) {
            if (!masterEvents[i]["IsOutright"]) {
                customObj = { isLive: masterEvents[i]["Live"], index: i }
                prefix = masterEvents[i]["Live"] ? MasterEventPrefix.Live : MasterEventPrefix.PreLive;
                this.newMasterEventsIds[prefix + masterEvents[i].ID] = customObj;
            }
        }
    }

    @action.bound setParams(branchId: number) {

        if (branchId) {
            this.currentBranchId = branchId.toString();
        }
    }

    // base on status we need to stop timer when we are on halftime for example
    /* istanbul ignore next */
    updateLiveTimeElapsedOnHalfTime(masterEvent: MasterEvent) {
        switch (parseInt(masterEvent.branchID)) {
            case eBranches.Soccer:
                if (masterEvent.gameData.Status === 3 || masterEvent.gameData.Status === 6) {
                    masterEvent.liveTimeElapsed = 0;
                }
                break;
            case eBranches.Basketball:
                if (masterEvent.gameData.Status >= 31 && masterEvent.gameData.Status <= 33) {
                    masterEvent.liveTimeElapsed = 0;
                }
                break;
            case eBranches.IceHockey:
                if (masterEvent.gameData.Status >= 31 && masterEvent.gameData.Status <= 32) {
                    masterEvent.liveTimeElapsed = 0;
                }
                break;
            default:
                break;
        }
    }

    extractMasterEvent(index: number, isLive: Boolean, eventsCacheLive: LiveMasterEventLegacy[], eventsCachePreLive: PreLiveMasterEventLegacy[]): MasterEvent {
        let masterEvent: MasterEvent;
        if (isLive) {
            masterEvent = Parsers.MasterEventParser.ParseLive(eventsCacheLive[index], liveEventsTime, this.oddsUtil, this.gameStatus)
        } else {
            masterEvent = Parsers.MasterEventParser.ParsePreLive(eventsCachePreLive[index], this.oddsUtil, this.currentTimeZone);
            masterEvent.statisticsInfo = this.getStatisticsInfo(eventsCachePreLive[index]);
            masterEvent.statisticsInfoClickHandler = this.getStatisticsInfoClickHandler(masterEvent.statisticsInfo, masterEvent);
        }

        masterEvent.selectionHandler = this.getSelectionHandler.bind(this, masterEvent);

        return masterEvent;
    }

    extractLeague(leagueId: string, leaguesCache: LeagueInfoLegacy[]): MultiViewLeagueInfo | undefined {

        let league = leaguesCache.find((league) => {
            return league.ID.toString() === leagueId;
        })

        if (!league) { return undefined; }
        let leagueModel = Parsers.MultiViewLeagueInfoParser.Parse(league);

        return leagueModel;
    }

    extractBranch(branchId: string, branchesCache: BranchInfoLegacy[]): MultiViewBranchInfo | undefined {
        let branch = branchesCache.find((branch) => {
            return branch.ID.toString() === branchId;
        })

        if (!branch) { return undefined; }

        let branchConfig = this.branchesConfigData.find((branchConfig) => branchConfig.BranchID.toString() === branchId);
        let branchModel = Parsers.MultiViewBranchInfoParser.Parse(branch, branchConfig);

        return branchModel;
    }

    getNumberOfLeagueEvents(branch: MultiViewBranchType) {
        let leagueEvents = 0;
        branch.leagueIds.forEach(leagueId => {
            let league = this.leaguesList.find((league) => league.id === leagueId);
            league && (leagueEvents += league.eventIds.length);
        });

        return leagueEvents;
    }

    getStatisticsInfo(event: PreLiveMasterEventLegacy): any | undefined {
        const branch = this.branchesList.find((branch) => branch.id === this.currentBranchId);
        const useStatistics = this.statisticsProvider.useStatistics && this.statisticsProvider.factory.isStatsAvailableMainEvent(event, branch);

        if (branch === undefined || !useStatistics) {
            return undefined;
        }
        return {
            betRadarData: { clientId: this.statisticsProvider.betRadarClientId, eventId: !event.BetRadarStatisticsEventId ? 0 : event.BetRadarStatisticsEventId },
            enetPulseData: { homeTeamId: event.homeEnetplusID, awayTeamId: event.awayEnetplusID, branchId: Number(branch.enetPulseID) }
        };
    }

    isEventInKickOff(eventDate: Date) {
        let eventStartDateMs = eventDate.getTime();
        let dateNow = this.timeControl.getServerTime().getTime();
        return (eventStartDateMs - dateNow) / 60000 <= this.startLiveBettingInMinutes;
    }

    getStatisticsInfoClickHandler(statisticsInfo, masterEvent: MasterEvent): Function | undefined {
        if (statisticsInfo === undefined) {
            return;
        } else {
            return this.openStatsHandler.bind(this, statisticsInfo);
        }
    }

    openStatsHandler(statisticsInfo, ev) {
        if (ev === undefined) {
            return;
        }
        ev.preventDefault();
        ev.stopPropagation();
        this.statisticsProvider.factory.openStats(statisticsInfo, ev);
    }

    @computed get currentBranch() {
        let branch = this.branchesList.find((branch) => branch.id === this.currentBranchId);
        this.currentSelectedBranch = branch;
        return branch;
    }

    getEvent = createTransformer((eventId: string) => {
        return this.masterEvents[eventId];
    });

    /* istanbul ignore next */
    @computed get getSortedLeaguesByBranch() {
        let sortedLeaguesByBranch: MultiViewLeagueInfo[] = [];
        let leagueIdsByBranch: String[] = [];

        if (this.currentBranch) {
            this.currentBranch.leagueIds;
            leagueIdsByBranch = this.currentBranch.leagueIds
        }

        leagueIdsByBranch.forEach(leagueId => {
            let league = this.leaguesList.find((league) => league.id === leagueId);
            if (league) {
                sortedLeaguesByBranch.push(league);
            }
        });

        // Chain sort provide us sort by -> then by
        /* istanbul ignore next */
        sortedLeaguesByBranch.sort(DataHelper.chainSort(
            function (l1: MultiViewLeagueInfo, l2: MultiViewLeagueInfo) {
                return l1.rankId - l2.rankId;
            },
            function (l1: MultiViewLeagueInfo, l2: MultiViewLeagueInfo) {
                return l1.sortId - l2.sortId;
            }
        ));

        return sortedLeaguesByBranch;
    };

    /* istanbul ignore next */
    @Debounce(1100)
    refreshData() {
        this.updateData();
    }

    /* istanbul ignore next */
    getSelectionHandler(event: MasterEvent, market: Market, selection: Selection) {
        // (masterEventID, eventID, lineID, odds, lineGroupID, lineTypeID, rowTypeID,
        // points, isLive, isQA, eventTypeId, leagueId, betId, preventUpdating,
        // isFavedTeam)
        let isFavedTeam, betId, preventUpdating;
        selection.type === SelectionType.HC && (isFavedTeam = selection.points < 0);

        return this.betSlipUtil.addOddToSlip(parseInt(event.id), parseInt(market.id), parseInt(selection.id), selection.odds, market.lineGroupID ? parseInt(market.lineGroupID) : 0,
            selection.lineType, (selection.type === "OU" && selection.position === Position.Center) ? Position.Left : selection.position, selection.points, event.isLive, market.isQA, parseInt(market.typeID),
            parseInt(event.leagueID), betId, preventUpdating, isFavedTeam);
    }

    openEventView(masterEvent: MasterEvent, event) {
        // TODO: Add check for outright
        let evTypeForGameBlock = masterEvent.isLive ? 1 : 0;
        this.navHandler.OpenGame(event, parseInt(masterEvent.id), masterEvent.isLive, evTypeForGameBlock, false, parseInt(masterEvent.branchID));
    }

    @action.bound createEmptyMarket(marketKey: string, numberOfSelections: number): Market {
        let selections = new Array();
        for (let i = 0; i < numberOfSelections; i++) {
            selections.push(new Selection({
                id: "",
                title: "",
                odds: NaN,
                position: Position.Left,
                type: SelectionType.ML,
                lineType: 0,
                points: 0
            }));
        }

        return new Market({
            id: "",
            isQA: false,
            typeID: "-1",
            typeName: "",
            selections
        });
    }

    @action.bound addOUStaticSelection(market) {
        (market.selections[0].type === SelectionType.OU && market.selections.length === 2) &&
            market.selections.push(new Selection({
                id: "",
                title: market.selections[0].points.toString(),
                odds: NaN,
                position: Position.Left,
                type: SelectionType.OU,
                lineType: -1,
                points: market.selections[0].points
            }))
    }

    /* istanbul ignore next */
    fillMissingSelections(currMarket: Market, numberOfSelections: number) {
        let positions = numberOfSelections === 3 ?
            [Position.Left, Position.Center, Position.Right] : [Position.Left, Position.Right];

        let currentPositions: Position[] = currMarket.selections.map((selection) => selection.position);
        let diff = positions.filter((position) => currentPositions.indexOf(position) < 0)
        diff.forEach((missingPosition) => {
            currMarket.selections.push(new Selection({
                id: "",
                title: "",
                odds: NaN,
                position: missingPosition,
                type: SelectionType.QA,
                lineType: -1,
                points: 0
            }))
        })
    }

    /* tslint:disable:cyclomatic-complexity */
    getEventMarkets(event: MasterEvent) {
        let eventMarkets = {};

        // for each market of branch config
        // find responding event market or set empty market
        let branchConfig = this.branchesConfigData.find((branchConfig) => branchConfig.BranchID.toString() === this.currentBranchId);

        branchConfig && branchConfig.MarketKeys.forEach((key) => {
            let marketKey = constants.typeIDMap[key]; //
            let currMarket = event.markets.find((market) => market.typeID === key);
            // if its OU market - add 'Points' disabled selection
            currMarket && this.addOUStaticSelection(currMarket);

            let currentBranch = this.currentBranch;
            let currentBranchMarketNames = currentBranch ? currentBranch.marketNames : {};
            let numberOfSelections = currentBranchMarketNames && currentBranchMarketNames[marketKey] ?
                currentBranchMarketNames[marketKey].length : 0;

            if (!eventMarkets[marketKey] || !eventMarkets[marketKey].id) {
                eventMarkets[marketKey] = currMarket || this.createEmptyMarket(marketKey, numberOfSelections)
            }

            // when we have market but the selections are not the correct number
            /* istanbul ignore next */
            if (currMarket && numberOfSelections !== currMarket.selections.length) {
                this.fillMissingSelections(currMarket, numberOfSelections);
            }

        });

        branchConfig &&
            (
                branchConfig.BranchID === eBranches.Basketball ||
                branchConfig.BranchID === eBranches.Tennis
            ) &&
            eventMarkets["FT_ML"] &&
            eventMarkets["FT_ML"].selections.length === 3 &&
            eventMarkets["FT_ML"].selections.splice(1, 1);

        return eventMarkets;
    }
}